import React, { useState } from 'react';
import PageHero from './PageHero';
import { Sparkles, MapPin, CheckCircle2, ChevronRight, X, Calendar, Compass, Shield, HeartHandshake } from 'lucide-react';
import heroImg from '../assets/GMIS Backyard View.png';
import campusBackyard from '../assets/GMIS Backyard View.png';
import studentsImg from '../assets/Banner-GMIS Students.png';
import juniorImg from '../assets/JUNIOR SCHOOL.png';
import primaryImg from '../assets/PRIMARY SCHOOL.png';
import seniorImg from '../assets/SENIOR SCHOOL.png';
import preschoolImg from '../assets/PRESCHOOL.png';

const FACILITIES_DATA = [
  {
    id: 'classrooms',
    title: 'Smart Interactive Classrooms',
    category: 'academic',
    categoryLabel: 'Academic & STEM',
    image: preschoolImg,
    badge: 'Technology-Enabled',
    desc: 'Spacious, climate-controlled classrooms equipped with high-definition interactive smartboards, ergonomic modular seating, and natural daylight designed to support collaborative and inquiry-led learning.',
    specs: [
      'Interactive Smart Display Boards with dual-touch capability',
      'Flexible modular furniture adaptable for individual and group work',
      'High-speed campus-wide filtered Wi-Fi and digital charging hubs',
      'Acoustic sound-dampening walls to maximize auditory focus',
    ],
  },
  {
    id: 'science-labs',
    title: 'Advanced Science & Innovation Labs',
    category: 'academic',
    categoryLabel: 'Academic & STEM',
    image: seniorImg,
    badge: 'IB & Cambridge Certified',
    desc: 'Dedicated multidisciplinary laboratories for Biology, Chemistry, and Physics featuring fume hoods, precision digital measuring instruments, and safety apparatus adhering to strict international school standards.',
    specs: [
      'Dedicated Biology, Chemistry, and Physics laboratory wings',
      'Microscopy stations, digital sensors, and analytical balances',
      'Emergency eye-wash stations, safety showers, and modern fume extractors',
      'Interactive prep rooms and chemical storage meeting international safety codes',
    ],
  },
  {
    id: 'robotics-makerspace',
    title: 'Robotics & STEM Makerspace',
    category: 'academic',
    categoryLabel: 'Academic & STEM',
    image: juniorImg,
    badge: 'Future-Ready Tech',
    desc: 'A hands-on engineering lab where students explore coding, algorithmic design, 3D printing, and robotics competitions from elementary through high school.',
    specs: [
      '3D printers, laser cutters, and rapid prototyping materials',
      'LEGO Mindstorms, VEX Robotics, and Arduino programming kits',
      'Dedicated arena mats for regional and global robotics challenges',
      'Collaborative design tables with dedicated Mac & PC workstations',
    ],
  },
  {
    id: 'swimming-pool',
    title: 'Semi-Olympic Swimming Complex',
    category: 'sports',
    categoryLabel: 'Sports & Athletics',
    image: campusBackyard,
    badge: 'Aquatic Center',
    desc: 'A covered 25-meter swimming facility accompanied by a dedicated training pool for younger learners, complete with certified lifeguards and spectator seating.',
    specs: [
      '25-meter temperature-regulated pool with 6 competitive lanes',
      'Shallow learn-to-swim zone dedicated to Early Years and Primary pupils',
      'Full-time certified lifeguards and water safety monitoring',
      'Modern locker rooms, warm showers, and elevated viewing gallery',
    ],
  },
  {
    id: 'gymnasium',
    title: 'Multipurpose Indoor Sports Arena',
    category: 'sports',
    categoryLabel: 'Sports & Athletics',
    image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=800&q=80',
    badge: 'Multi-Sport Arena',
    desc: 'A high-ceiling indoor sports hall equipped with shock-absorbent sprung hardwood flooring catering to basketball, volleyball, badminton, gymnastics, and school assemblies.',
    specs: [
      'FIBA-regulation basketball court and 4 badminton courts',
      'Retractable electronic scoreboards and high-lumen anti-glare sports lighting',
      'Professional shock-absorbent sprung maple flooring',
      'Bleacher seating accommodating up to 600 enthusiastic spectators',
    ],
  },
  {
    id: 'sports-field',
    title: 'Outdoor Football Field & Running Track',
    category: 'sports',
    categoryLabel: 'Sports & Athletics',
    image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=800&q=80',
    badge: 'Outdoor Athletics',
    desc: 'Expansive natural turf pitch for football, cricket, and track athletics, flanked by shaded spectator perimeters and athletics training infrastructure.',
    specs: [
      'Full-sized football pitch maintained year-round',
      'Perimeter running track and long-jump sand pit',
      'Covered player dugouts and high-output floodlights for evening fixtures',
      'Dedicated cricket practice nets and archery safety zones',
    ],
  },
  {
    id: 'auditorium',
    title: 'Grand Performing Arts Auditorium',
    category: 'arts',
    categoryLabel: 'Arts & Performing Spaces',
    image: 'https://images.unsplash.com/photo-1514306191717-452ec28c7814?auto=format&fit=crop&w=800&q=80',
    badge: '500-Seat Theatre',
    desc: 'A state-of-the-art auditorium designed for school theatrical productions, musical concerts, guest lectures, graduation ceremonies, and cultural festivals.',
    specs: [
      '500 plush tiered theatre seats with unhindered sightlines',
      'Motorized theatrical fly system, digital lighting grid, and follow-spots',
      'Concert-grade digital audio mixing console and surround acoustics',
      'Backstage dressing suites, green rooms, and prop storage workshops',
    ],
  },
  {
    id: 'music-studio',
    title: 'Music Suites & Sound Studios',
    category: 'arts',
    categoryLabel: 'Arts & Performing Spaces',
    image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=800&q=80',
    badge: 'Acoustically Treated',
    desc: 'Sound-isolated instrument practice rooms, choral rehearsal spaces, and a digital recording suite where students compose, record, and master original musical works.',
    specs: [
      'Individual soundproof acoustic rehearsal booths for piano and winds',
      'Full ensemble orchestral room equipped with percussion and keyboards',
      'Digital Audio Workstation (DAW) lab featuring MIDI keyboards and synthesizers',
      'Extensive collection of Western, Indian, and Indonesian traditional instruments',
    ],
  },
  {
    id: 'visual-arts',
    title: 'Visual & Ceramic Arts Studios',
    category: 'arts',
    categoryLabel: 'Arts & Performing Spaces',
    image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=800&q=80',
    badge: 'Creative Studio',
    desc: 'Sun-drenched art studios with floor-to-ceiling windows, pottery wheels, kiln firing facilities, and printmaking presses to inspire budding sculptors and painters.',
    specs: [
      'North-facing panoramic natural daylight for accurate color representation',
      'Electric pottery wheels, wet-clay recycling station, and ceramic kiln',
      'Etching, screen-printing, and textile design equipment',
      'Student gallery exhibition corridor showcasing term portfolios',
    ],
  },
  {
    id: 'library',
    title: 'Learning Resource & Media Centre',
    category: 'wellness',
    categoryLabel: 'Campus Life & Wellness',
    image: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=800&q=80',
    badge: 'Multi-Lingual Hub',
    desc: 'A two-story library housing over 40,000 print volumes, scholarly databases, quiet reading nooks, and collaborative study pods supporting multilingual inquiry.',
    specs: [
      'Extensive physical collection across English, Hindi, Mandarin, and Bahasa Indonesia',
      'Subscription access to JSTOR, EBSCOhost, and Britannica School databases',
      'Soundproof discussion rooms with smart presentation screens',
      'Dedicated primary junior reading corner with beanbags and early readers',
    ],
  },
  {
    id: 'sky-garden',
    title: 'Sky Garden & Green Eco Spaces',
    category: 'wellness',
    categoryLabel: 'Campus Life & Wellness',
    image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=800&q=80',
    badge: 'Well-being Zones',
    desc: 'Lush landscaped green courtyards and open-air rooftop terraces that offer tranquil respite between classes, fostering mental well-being and ecological connection.',
    specs: [
      'Botanical learning gardens maintained by the Student Eco-Club',
      'Shaded pergola seating for quiet study and peer socialization',
      'Rainwater harvesting and compost sustainability demonstration stations',
      'Open-air event terrace overlooking Kemayoran urban landscape',
    ],
  },
  {
    id: 'cafeteria',
    title: 'Student Dining Hall & Health Clinic',
    category: 'wellness',
    categoryLabel: 'Campus Life & Wellness',
    image: 'https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=800&q=80',
    badge: 'Health & Nutrition',
    desc: 'A hygienic, spacious dining pavilion offering freshly cooked nutritious meals catering to diverse dietary choices, alongside a full-time medical health clinic.',
    specs: [
      'Nutritious hot lunch counters featuring vegetarian, halal, and international menus',
      'Strict food hygiene standards certified by public health authorities',
      'Full-time certified registered nurses and doctor-on-call facility',
      'Private observation beds, first-aid response, and health screening suites',
    ],
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All Facilities' },
  { id: 'academic', label: 'Academic & STEM' },
  { id: 'sports', label: 'Sports & Athletics' },
  { id: 'arts', label: 'Arts & Performing' },
  { id: 'wellness', label: 'Campus Life & Wellness' },
];

export default function SchoolFacilitiesPage({ setPage, t }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedFacility, setSelectedFacility] = useState(null);

  const filteredFacilities =
    activeCategory === 'all'
      ? FACILITIES_DATA
      : FACILITIES_DATA.filter((f) => f.category === activeCategory);

  const navCategory = t?.nav?.[1]?.label || "About Us";
  const homeLabel = t?.nav?.[0]?.label || "Home";

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Page Hero ──────────────────────────────────────────────────────── */}
      <PageHero
        title="Campus Facilities"
        eyebrow={navCategory}
        subtitle="A world-class 9-acre urban campus thoughtfully designed to inspire academic mastery, athletic excellence, creative expression, and student well-being."
        breadcrumbs={[homeLabel, navCategory, 'School Facilities']}
        bgImage={heroImg}
      />

      <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px] py-16 lg:py-24">

        {/* ── Section 1: Campus Overview & Philosophy ──────────────────────── */}
        <section className="mb-20">
          <div className="bg-white rounded-3xl border border-line p-8 lg:p-14 shadow-[0_10px_40px_rgba(14,36,62,0.05)] text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
              <Sparkles className="w-4 h-4 text-gold-ink" />
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">Inspiring Environment</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-navy uppercase tracking-tight mb-5 leading-tight">
              Spaces Designed for Exploration, Discovery & Well-Being
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto mb-6" />
            <p className="text-ink/80 text-[16.5px] leading-relaxed mb-10 max-w-3xl mx-auto">
              GMIS Jakarta’s Kemayoran campus provides a vibrant, secure, and modern learning ecosystem. Every facility — from advanced robotics laboratories and swimming pools to serene green courtyards — is purpose-built to nurture curious, capable, and well-rounded global citizens.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-line text-center">
              <div className="p-4 rounded-xl bg-paper">
                <p className="text-3xl font-heading font-bold text-navy">50+</p>
                <p className="text-xs font-semibold text-gold uppercase tracking-wider mt-1">Smart Classrooms</p>
              </div>
              <div className="p-4 rounded-xl bg-paper">
                <p className="text-3xl font-heading font-bold text-navy">6</p>
                <p className="text-xs font-semibold text-gold uppercase tracking-wider mt-1">Science & Tech Labs</p>
              </div>
              <div className="p-4 rounded-xl bg-paper">
                <p className="text-3xl font-heading font-bold text-navy">25m</p>
                <p className="text-xs font-semibold text-gold uppercase tracking-wider mt-1">Aquatic Center</p>
              </div>
              <div className="p-4 rounded-xl bg-paper">
                <p className="text-3xl font-heading font-bold text-navy">500</p>
                <p className="text-xs font-semibold text-gold uppercase tracking-wider mt-1">Seat Auditorium</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 2: Category Filter Tabs ──────────────────────────────── */}
        <section className="mb-12">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer border ${
                  activeCategory === cat.id
                    ? 'bg-navy-deep text-gold border-navy-deep shadow-md'
                    : 'bg-white text-ink/75 border-line hover:border-gold hover:text-navy'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </section>

        {/* ── Section 3: Facilities Cards Grid ─────────────────────────────── */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredFacilities.map((facility) => (
              <div
                key={facility.id}
                className="bg-white rounded-2xl border border-line overflow-hidden shadow-[0_8px_30px_rgba(14,36,62,0.04)] hover:shadow-[0_16px_40px_rgba(14,36,62,0.1)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1.5"
              >
                <div>
                  <div className="h-56 w-full overflow-hidden relative bg-navy/5">
                    <img
                      src={facility.image}
                      alt={facility.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-navy-deep/85 backdrop-blur-sm text-gold text-[10.5px] font-bold tracking-wider uppercase">
                        {facility.categoryLabel}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <span className="px-2.5 py-0.5 rounded bg-white/90 backdrop-blur-sm text-navy text-[10px] font-bold tracking-wide uppercase shadow-sm">
                        {facility.badge}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-heading font-bold text-navy mb-2 leading-snug group-hover:text-gold transition-colors">
                      {facility.title}
                    </h3>
                    <p className="text-sm text-ink/75 leading-relaxed mb-5 line-clamp-3">
                      {facility.desc}
                    </p>

                    <div className="space-y-1.5 pt-3 border-t border-line/60">
                      {facility.specs.slice(0, 2).map((s, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-ink/70">
                          <CheckCircle2 className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" />
                          <span className="truncate">{s}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6 pt-2 border-t border-line flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setSelectedFacility(facility)}
                    className="text-xs font-bold text-navy hover:text-gold transition-colors inline-flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>View Specifications</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[11px] font-medium text-gold uppercase tracking-wider">
                    Kemayoran Campus
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 4: Campus Safety & Eco Standards ─────────────────────── */}
        <section className="bg-white rounded-3xl border border-line p-8 lg:p-12 mb-20 shadow-sm">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col gap-3">
              <div className="w-12 h-12 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center">
                <Shield className="w-6 h-6" />
              </div>
              <h4 className="font-heading font-bold text-navy text-lg uppercase">
                24/7 Security & Access Control
              </h4>
              <p className="text-sm text-ink/70 leading-relaxed">
                Full-perimeter security personnel, gated entry checkpoints, digital visitor management, and CCTV monitoring ensure complete peace of mind.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="w-12 h-12 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center">
                <Compass className="w-6 h-6" />
              </div>
              <h4 className="font-heading font-bold text-navy text-lg uppercase">
                Central Kemayoran Location
              </h4>
              <p className="text-sm text-ink/70 leading-relaxed">
                Conveniently situated along Jl. HBR Motik in Central Jakarta, with dedicated bus transportation routes covering North, Central, West, and South Jakarta.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="w-12 h-12 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center">
                <HeartHandshake className="w-6 h-6" />
              </div>
              <h4 className="font-heading font-bold text-navy text-lg uppercase">
                Community & Facility Rental
              </h4>
              <p className="text-sm text-ink/70 leading-relaxed">
                Our sports arena, auditorium, and fields are available for educational conferences, athletic competitions, and partner community organizations.
              </p>
            </div>
          </div>
        </section>

        {/* ── Section 5: Schedule a Visit CTA Strip ────────────────────────── */}
        <section className="bg-navy-deep text-white rounded-3xl p-10 lg:p-14 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-navy/40 pointer-events-none" />
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-heading font-bold text-white uppercase tracking-tight mb-4">
              Experience Our Campus First-Hand
            </h2>
            <p className="text-white/80 text-base leading-relaxed mb-8">
              There is no substitute for walking our halls, exploring the labs, and seeing our vibrant community in action. Book your personalized campus tour today.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  if (setPage) setPage('schedule-visit');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="px-8 py-3.5 rounded-full bg-gold text-navy-deep font-bold text-xs uppercase tracking-widest hover:brightness-110 transition-all shadow-md cursor-pointer border-0"
              >
                Schedule a Visit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (setPage) setPage('contact');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="px-8 py-3.5 rounded-full bg-white/10 text-white font-bold text-xs uppercase tracking-widest hover:bg-white/20 transition-all border border-white/20 cursor-pointer"
              >
                Contact Admissions
              </button>
            </div>
          </div>
        </section>

      </div>

      {/* ── Facility Specifications Modal ──────────────────────────────────── */}
      {selectedFacility && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-deep/75 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 lg:p-10 relative shadow-2xl border border-line max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setSelectedFacility(null)}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-navy/5 hover:bg-gold/20 text-navy flex items-center justify-center transition-colors cursor-pointer border-0"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="h-64 w-full rounded-2xl overflow-hidden mb-6 relative">
              <img
                src={selectedFacility.image}
                alt={selectedFacility.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 rounded-full bg-navy-deep/85 text-gold text-xs font-bold uppercase tracking-wider">
                  {selectedFacility.categoryLabel}
                </span>
              </div>
            </div>

            <h3 className="text-2xl font-heading font-bold text-navy mb-2">
              {selectedFacility.title}
            </h3>
            <p className="text-sm text-ink/80 leading-relaxed mb-6">
              {selectedFacility.desc}
            </p>

            <div className="bg-paper p-5 rounded-2xl border border-line mb-6">
              <h4 className="text-xs font-bold text-navy uppercase tracking-wider mb-3">
                Key Facility Specifications & Highlights
              </h4>
              <div className="space-y-2.5">
                {selectedFacility.specs.map((spec, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-xs text-ink/85">
                    <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                    <span>{spec}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-line flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-ink/60">
                <MapPin className="w-4 h-4 text-gold" />
                <span>Gandhi Memorial Intercontinental School, Kemayoran</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedFacility(null);
                  if (setPage) setPage('schedule-visit');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="px-6 py-2.5 rounded-full bg-navy-deep text-gold font-bold text-xs uppercase tracking-wider hover:bg-navy transition-colors cursor-pointer border-0"
              >
                Book a Visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
